import React, { useState, useRef, useEffect } from 'react'; // Import useState, useRef, useEffect
import clsx from 'clsx';
import { TempFooterProps, IInclusionIcon } from './Footer.interface';
import './Footer.styles.css';
import BlockWrapper from '@/layouts/BlockWrapper/BlockWrapper.component';
import ContentWrapper, {
    ContentColumn,
} from '@/layouts/ContentWrapper/ContentWrapper.component';
import Image from '@/elements/Image/Image.component';
import Text from '@/elements/Text/Text.component';
import Heading from '@/elements/Heading/Heading.component';
import Icon from '@/elements/Icon/Icon.component';
import SupportCta from '../SupportCta/SupportCta.component';

const MAX_INCLUSION_ICONS = 6;
const MAX_INCLUSION_LOGOS = 2;

const isInclusionIconDecorative = (icon: IInclusionIcon): boolean =>
    icon.announceToScreenReader === false ||
    (!icon.announceToScreenReader && !icon.image?.alternateText);

const Footer: React.FC<TempFooterProps> = ({
    className,
    welcomeText,
    inclusionLogos,
    inclusionIcons,
    inclusionBackgroundColor,
    logo,
    socialLinks,
    linkLists,
    copyrightText,
    legalLinks,
    supportCta,
    hideFooterBanner,
    ...blockWrapperProps
}: TempFooterProps) => {
    // State to track the active link index for each list
    const [activeIndices, setActiveIndices] = useState<{
        [listIndex: number]: number
    } | null>(null);
    // Refs for each list UL element
    const listRefs = useRef<(HTMLUListElement)[]>([]);

    // // Initialize active indices and refs array size when linkLists change
    useEffect(() => {

        if (!linkLists || linkLists.length === 0) {
            return;
        }
        const initialActiveIndices = linkLists.reduce(
            (acc, _, index) => {
                acc[index] = 0; // Default first item active
                return acc;
            },
            {} as { [listIndex: number]: number },
        );
        setActiveIndices(initialActiveIndices);
        // Ensure refs array is the correct size
        listRefs.current = listRefs.current.slice(0, linkLists.length);
    }, [linkLists]);

    // Roving tabindex implementation for link lists using state and refs
    const handleListKeyDown = (
        e: React.KeyboardEvent<HTMLUListElement>,
        listIndex: number,
    ) => {
        const list = listRefs.current[listIndex];
        if (!list) return;

        // Get links within the specific list UL
        const links = Array.from(
            list.querySelectorAll<HTMLAnchorElement>('.footer__links--item'),
        );
        if (links.length === 0) return;

        const target = e.target as HTMLElement;
        // Ensure the event target is a link within the list
        const currentIndex = links.findIndex((link) => link === target);
        if (currentIndex === -1) return; // Target isn't a link in this list

        let nextIndex = currentIndex;

        switch (e.key) {
            case 'ArrowDown':
            case 'ArrowRight':
                nextIndex = (currentIndex + 1) % links.length;
                break;
            case 'ArrowUp':
            case 'ArrowLeft':
                nextIndex = (currentIndex - 1 + links.length) % links.length;
                break;
            case 'Home':
                nextIndex = 0;
                break;
            case 'End':
                nextIndex = links.length - 1;
                break;
            default:
                return; // Exit if key is not handled
        }

        if (nextIndex !== currentIndex) {
            e.preventDefault(); // Prevent page scroll only if we handle the key
            // Update state to reflect the new active index for this list
            setActiveIndices((prev) => ({ ...prev, [listIndex]: nextIndex }));
            // Focus the newly active link
            // Use requestAnimationFrame to ensure focus happens after potential re-render due to state change
            requestAnimationFrame(() => {
                links[nextIndex]?.focus();
            });
            // Or potentially simpler, if state updates + re-renders don't interfere:
            // links[nextIndex]?.focus();
        }
    };

    const visibleInclusionLogos =
        inclusionLogos
            ?.filter((logoItem) => Boolean(logoItem?.defaultSrc))
            .slice(0, MAX_INCLUSION_LOGOS) ?? [];
    const visibleInclusionIcons =
        inclusionIcons
            ?.filter((icon) => Boolean(icon?.image?.defaultSrc))
            .slice(0, MAX_INCLUSION_ICONS) ?? [];
    const hasInclusionLogos = visibleInclusionLogos.length > 0;
    const hasInclusionIcons = visibleInclusionIcons.length > 0;
    const showInclusionSection = Boolean(welcomeText || hasInclusionLogos || hasInclusionIcons);
    const areAllIconsDecorative = visibleInclusionIcons.every(isInclusionIconDecorative);

    //TODO: Remove the paddingBottom prop from SupportCta once the Footer has been split
    return (
        <>
            {!hideFooterBanner && supportCta && <SupportCta {...supportCta} paddingTop="small" paddingBottom="small" />}
            <footer className="footer">
                {showInclusionSection && (
                    <BlockWrapper
                        className={clsx(
                            'footer__welcome',
                            !inclusionBackgroundColor && 'footer__welcome--defaultBg',
                            className,
                        )}
                        backgroundColor={inclusionBackgroundColor}
                    >
                        {/* Row 1: Inclusion / acknowledgement */}
                        <ContentWrapper className="footer__welcome--wrapper">
                            {welcomeText && (
                                <Text
                                    as="div"
                                    className="footer__welcome--text"
                                    html={welcomeText}
                                />
                            )}
                            {(hasInclusionLogos || hasInclusionIcons) && (
                                <div className="footer__welcome--media">
                                    {hasInclusionLogos && (
                                        <div className="footer__welcome--logos">
                                            {visibleInclusionLogos.map((logoItem, index) => (
                                                <Image
                                                    key={`inclusion-logo-${index}`}
                                                    {...logoItem}
                                                    className="footer__welcome--logo"
                                                />
                                            ))}
                                        </div>
                                    )}
                                    {hasInclusionIcons && (
                                        <ul
                                            className="footer__welcome--icons"
                                            aria-hidden={areAllIconsDecorative ? true : undefined}
                                        >
                                            {visibleInclusionIcons.map((icon, index) => {
                                                const isDecorative = isInclusionIconDecorative(icon);
                                                return (
                                                    <li
                                                        key={`inclusion-icon-${index}`}
                                                        className="footer__welcome--icon"
                                                        aria-hidden={isDecorative ? true : undefined}
                                                    >
                                                        <Image
                                                            {...icon.image}
                                                            alternateText={
                                                                isDecorative
                                                                    ? ''
                                                                    : icon.image.alternateText
                                                            }
                                                        />
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </ContentWrapper>
                    </BlockWrapper>
                )}
                <BlockWrapper
                    className={clsx('footer__main', className)}
                    {...blockWrapperProps}
                >
                    {/* Row 2: Logo and Socials */}
                    <ContentWrapper className="footer__branding">
                        <ContentColumn className="footer__branding--content">
                            {logo && <Image {...logo} className="footer__branding--logo" />}
                            {socialLinks && socialLinks.length > 0 && (
                                <ul className="footer__branding--socials">
                                    {socialLinks.map((link, index) => (
                                        <li key={`social-${link.icon}-${index}`}>
                                            <a
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={link.label}
                                                className="footer__branding--socialLink"
                                            >
                                                <Icon icon={link.icon} />
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </ContentColumn>
                    </ContentWrapper>

                    {/* Row 3: Link Lists */}
                    <ContentWrapper className="footer__links">
                        <ContentColumn className="footer__links--content">
                            {linkLists &&
                                linkLists.length > 0 ?
                                linkLists.map((list, listIndex) => (
                                    <div
                                        key={`footer-linklist-${listIndex}`}
                                        className="footer__links--list"
                                    >
                                        {list.title && (
                                            <a
                                                href={list.title.href}
                                                className="footer__links--titlelink"
                                                target={list.title.target || "_self"}
                                                aria-label={list.title.ariaLabel || ''}
                                                title={list.title.title || ''}
                                            >
                                                <Heading level={5} className="footer__links--title" html={list.title.text || list.title.title}  />
                                            </a>
                                        )}
                                        {list.links && list.links.length > 0 && (
                                            <ul
                                                className="footer__links--items"
                                                role="list"
                                                // Pass listIndex to the handler
                                                onKeyDown={(e) => handleListKeyDown(e, listIndex)}
                                                // Assign ref to the UL element
                                                ref={(el) => {
                                                    if (el) {
                                                        listRefs.current[listIndex] = el;
                                                    }
                                                }}
                                            >
                                                {list.links.map((link, linkIndex) => (
                                                    <li
                                                        key={`linklist-${listIndex}-item-${linkIndex}`}
                                                        role="listitem"
                                                    >
                                                        <a
                                                            href={link.href}
                                                            className="footer__links--item"
                                                            // Set tabindex based on active state for this list
                                                            tabIndex={
                                                                activeIndices && activeIndices[listIndex] === linkIndex ? 0 : -1
                                                            }
                                                            target={link.target || "_self"}
                                                            aria-label={link.ariaLabel || ''}
                                                            title={link.title || ''}
                                                        >
                                                            {link.text || link.title}
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )) : null}
                        </ContentColumn>
                    </ContentWrapper>

                    {/* Row 4: Copyright and Legal */}
                    <ContentWrapper className="footer__legal">
                        <ContentColumn className="footer__legal--content">
                            {copyrightText && (
                                <Text
                                    className="footer__legal--copyright"
                                    html={copyrightText}
                                />
                            )}
                            {legalLinks && legalLinks.length > 0 && (
                                <ul className="footer__legal--links">
                                    {legalLinks.map((link, index) => (
                                        <li key={`legal-links-${index}`}>
                                            <a 
                                                href={link.href}
                                                className="footer__legal--link"
                                                target={link.target || "_self"}
                                                aria-label={link.ariaLabel || ''}
                                                title={link.title || ''}>
                                                {link.text || link.title}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </ContentColumn>
                    </ContentWrapper>
                </BlockWrapper>
            </footer>
        </>
    );
};
export { Footer };
